"use client"
import styles from "@css/modules/PointChart.module.scss"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import cn from "classnames"
import "nouislider/dist/nouislider.css"
import { cellTypeClasses, getCellData, getColumns, nightNames, useStickyHeader } from "@components/ResortChart/utils"
import { Header } from "@components/ResortChart/Header"
import { ColumnDescriptions } from "@components/ResortChart/ColumnDescriptions"
import { Resort } from "@t/Resort"
import { useRouter } from "next/navigation"

export type Search = { year: string; points: string; view: "days" | "points"; back?: string }
type Props = {
    years: number[]
    resort: Resort
    chart: any
    points: number
    pointsOrDays: boolean
    searchParams: Search
}

export function ResortChart(props: Props) {
    const chart = props.chart
    const searchParams = props.searchParams
    const [rows, setRows] = useState(() => {
        return (
            chart?.rows?.reduce((res, i) => {
                if (!res[i.period]) {
                    res[i.period] = []
                }
                res[i.period].push(i)
                return res
            }, {}) || {}
        )
    })
    const rootRef = useRef<HTMLDivElement>(null)
    const router = useRouter()
    useEffect(() => {
        setRows(
            chart.rows.reduce((res, i) => {
                if (!res[i.period]) {
                    res[i.period] = []
                }
                res[i.period].push(i)
                return res
            }, {})
        )
    }, [chart, props.pointsOrDays])

    const changeYear = useCallback((value: string) => {
        const params = new URLSearchParams(searchParams)
        params.set("year", value)
        router.push(`?${params.toString()}`)
    }, [])
    const columns = useMemo(() => getColumns(chart), [chart, props.pointsOrDays])
    useEffect(() => {
        const cell = document.querySelector<HTMLTableCellElement>(`.${styles.table} tbody tr:last-child td:first-child`)
        if (cell) {
            rootRef.current!.style.setProperty("--lastHeight", cell.offsetHeight + "px")
        }
        const elm = rootRef.current?.querySelector("." + styles.table + " thead")
        if (elm) {
            const observer = new ResizeObserver((entries) => {
                if (entries[0]) {
                    rootRef.current!.style.setProperty("--headHeight", entries[0].contentRect.height + "px")
                }
            })
            observer.observe(elm)
            return () => observer.disconnect()
        }
    }, [])
    useStickyHeader()

    return (
        <div className={styles.root} ref={rootRef}>
            <Header searchParams={searchParams} points={props.points} resort={props.resort} years={props.years} changeYear={changeYear} selectedYear={chart.year} />
            <ColumnDescriptions mobile={true} column_description={chart?.column_description} />
            <div className={styles.relativeContainer}>
                <div className={styles.tableWrapper}>
                    <table className={styles.emptyTable} cellSpacing={0} cellPadding={0}>
                        <thead>
                            <tr>
                                <th />
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {chart?.periods?.map((i) => {
                                const _rows = rows[i.group] || null
                                if (!_rows) return null
                                return (
                                    <tr key={i.group + "_" + chart.year}>
                                        <td>
                                            {_rows.map((j, key) => (
                                                <span key={key} className={styles.nights}>
                                                    1
                                                </span>
                                            ))}
                                        </td>
                                        <td width="50%" />
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>

                    <table className={styles.leftTable} cellSpacing={0} cellPadding={0}>
                        <thead>
                            <tr>
                                <th>Seasons</th>
                                <th>Nights</th>
                            </tr>
                        </thead>
                        <tbody>
                            {chart?.periods?.map((i) => {
                                const _rows = rows[i.group] || null
                                if (!_rows) return null
                                return (
                                    <tr key={i.group + "_" + chart.year}>
                                        <td>
                                            {i.item?.map((j, key) => (
                                                <span className={styles.period} key={key}>
                                                    {j.date}
                                                </span>
                                            ))}
                                        </td>
                                        <td>
                                            {_rows.map((j, key) => (
                                                <span className={styles.nights} key={key}>
                                                    {nightNames[j.nights] || j.nights}
                                                </span>
                                            ))}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                    <div className={styles.tableScroll}>
                        <table className={styles.table} cellSpacing={0} cellPadding={0}>
                            <thead>
                                <tr>
                                    {columns.map((i) => {
                                        return (
                                            <td key={i.key}>
                                                <span className={styles.columnTitle}> {i.title}</span>
                                                <span className={styles.columnSubTitle}>{i.sub_title}</span>
                                            </td>
                                        )
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    {columns.map((i) => {
                                        return (
                                            <td key={i.key}>
                                                <span className={styles.columnSymbols}>
                                                    {i.children.map((j) => (
                                                        <span
                                                            key={j.short}
                                                            title={j.long}
                                                            className={cn({
                                                                [styles.cellSymbol]: true,
                                                                [cellTypeClasses[j.key]]: true
                                                            })}>
                                                            {j.short}
                                                        </span>
                                                    ))}
                                                </span>
                                            </td>
                                        )
                                    })}
                                </tr>
                                {chart?.periods?.map((i) => {
                                    const _rows = rows[i.group] || null
                                    if (!_rows) return null
                                    const cells = _rows[0]?.cells || [] //_rows.cells
                                    return (
                                        <tr key={i.group + "_" + chart.year}>
                                            {cells.map((_, key) => (
                                                <td key={key}>{_rows.map((row, rowKey) => getCellData(row.cells[key], rowKey, props.points, props.pointsOrDays))}</td>
                                            ))}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                        <ColumnDescriptions mobile={false} column_description={chart?.column_description} />
                    </div>
                </div>
            </div>
        </div>
    )
}
